import { cn } from '@/lib/utils'

interface SegmentedToggleProps<T extends string> {
  options: readonly T[]
  value: T
  onChange: (value: T) => void
  size?: 'sm' | 'md'
  ariaLabel?: string
  /** Optional per-option suffix badge (e.g. a "Phase 2" tag). */
  badges?: Partial<Record<T, string>>
}

/** Reusable pill toggle matching the Cash Collections segmented controls. */
export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  ariaLabel,
  badges,
}: SegmentedToggleProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex items-center gap-0.5 rounded-full bg-stone-100 p-0.5 dark:bg-stone-800/70"
    >
      {options.map((opt) => {
        const active = opt === value
        return (
          <button
            key={opt}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(opt)}
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium transition-colors',
              size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
              active
                ? 'bg-white text-stone-900 shadow-sm ring-1 ring-stone-900/5 dark:bg-stone-950 dark:text-white'
                : 'text-stone-500 hover:text-stone-900 dark:text-stone-400 dark:hover:text-stone-100',
            )}
          >
            {opt}
            {badges?.[opt] && (
              <span className="rounded-full bg-champagne-100 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-champagne-700 dark:bg-champagne-500/20 dark:text-champagne-300">
                {badges[opt]}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
